import { isEmpty, find, get, filter, head, last, clone, uniqBy } from 'lodash';
import moment from 'moment';
import i18n from 'i18n';
import Platform from 'platform';
import { showLoader, hideLoader } from 'actions/loader';
import { showToast } from 'actions/toast';
import requester from 'utils/requester';
import getLocalized from 'utils/get-localized';
import getLocalizedDate from 'utils/get-localized-date';
import api from 'config/api';
import { getIsLoggedInSelector } from 'reducers/profile/selectors';
import { getChannelsDataSelector, getLiveEPGErrorTimestamp } from 'reducers/channels/selectors';
import * as types from './types';

import urlHelper from '../../helpers/urlhelper';
import config from 'config';

const FAVORITES_LIST_NAME = 'Favorite channels';

/**
 * Возвращает диапазон для запроса нового списка EPG
 * в зависимости от направления движения (2 дня вперед/назад)
 *
 * @param {Object} param0
 * @param {String} param0.startTime
 * @param {String} param0.endTime
 * @param {Boolean} moveForward
 *
 * @return {{start: Date, end: Date}}
 */
const getDateRange = ({ startTime, endTime }, moveForward) => {
  const step = 1;
  let start, end;

  if (moveForward) {
    start = moment(endTime);
    end = moment(startTime).add(step, 'days');
  } else {
    start = moment(startTime).subtract(step, 'days');
    end = moment(startTime);
  }
  return { start, end };
};

/**
 * Соединяет 2 списка в один в зависимости от "startToEnd"
 *
 * @param {Array} prev
 * @param {Array} next
 * @param {Boolean} startToEnd
 * @return {Array}
 */
const mergeArrays = (prev, next, startToEnd) => (startToEnd ? prev.concat(next) : next.concat(prev));

/**
 * Расширяет список программ залоговками по каждому дню
 * и переводами тайтлов
 *
 * @param {Object[]} programs
 * @return {Object[]}
 */
const convertProgramsToEPGList = (programs) => {
  const results = [];

  for (const program of programs) {
    const date = moment(program.startTime).format('YYYYMMDD');
    if (!find(results, { id: date })) {
      const dayTitle = {
        isHeader: true,
        id: date,
        title: getLocalizedDate(date),
      };
      results.push(dayTitle);
    }
    program.isHeader = false;
    program.subTitle = [moment(program.startTime).format('HH:mm'), moment(program.endTime).format('HH:mm')].join(' - ');
    results.push(program);
  }
  return results;
};

/*
 * Достает список сохраненных плейлистов
 * По особенностям бэкенда Воли первый элемент - это всегда есть
 * "Избранное"
 */
const getFavoriteChannelsList = async () => {
  const [firstList] = await requester({
    method: 'get',
    url: `${api.host}${api.endpoints.favoriteChannels}`,
  });
  return firstList;
};

const getCurrentProgramForChannelsByIds = async (ids, isLoggedIn) => {
  const date = moment().add(config.timeDiffFromServer, 'm').toISOString();

  const endpointEPG = urlHelper.getAuthorizedEndpointUrl(api.endpoints.epg(ids.join(','), date, date), isLoggedIn);

  return requester({
    method: 'get',
    url: `${api.host}${endpointEPG}`,
  });
};

const createChannelFavoritesList = async (channelId) =>
  requester({
    method: 'post',
    url: `${api.host}${api.endpoints.favoriteChannels}`,
    data: { name: FAVORITES_LIST_NAME, channelIds: [channelId] },
  });

export const fetchChannels = () => async (dispatch, getState) => {
  const isLoggedIn = getIsLoggedInSelector(getState());
  const endpointChannels = urlHelper.getAuthorizedEndpointUrl(api.endpoints.channels, isLoggedIn);

  try {
    dispatch(showLoader());
    dispatch({ type: types.FETCH });
    let requestParams = { visibilityRights: 'PREVIEW' };
    if (Platform.getDeviceType() !== 'OLD_TV') {
      requestParams['includeVod'] = true;
    }
    const channels = await requester({
      method: 'get',
      url: `${api.host}${endpointChannels}`,
      params: requestParams,
    });
    const programs = await getCurrentProgramForChannelsByIds(
      channels.map((channel) => channel.id),
      isLoggedIn,
    );

    const favoriteChannels = isLoggedIn ? await getFavoriteChannelsList() : null;
    const favoriteChannelIds = get(favoriteChannels, ['channelIds'], []);
    const data = channels.map((channel) => {
      const { id: channelId } = channel;
      const currentProgram = find(programs, { channelId }) || channel.currentProgram;

      if (currentProgram) {
        currentProgram.title = getLocalized(currentProgram.titles, currentProgram.title);
      }
      channel.isAdult = get(channel.pgRating, ['minRecomendedAge'], 0) === 18;
      channel.currentProgram = currentProgram;
      channel.title = getLocalized(channel.titles, channel.title);
      channel.notAvailable = channel.visibilityDetails === 'MISSING_SUBSCRIPTION';
      channel.isCatchUpAvailable = get(channel, ['tvServices', 'catchupTv'], false);
      channel.favorite = favoriteChannelIds.includes(channelId);
      return channel;
    });
    dispatch({ type: types.FETCH_SUCCESS, payload: { data } });
  } catch (error) {
    console.error(error);
    dispatch({ type: types.FETCH_ERROR, payload: { error: error.message } });
  } finally {
    dispatch(hideLoader());
  }
};

export const fetchEpg =
  ({ channelId, startTime }) =>
  async (dispatch, getState) => {
    startTime = startTime ?? moment().add(config.timeDiffFromServer, 'm');
    try {
      if (!channelId) return;
      dispatch({ type: types.FETCH_EPG, payload: { channelId } });

      const currentChannel = find(getChannelsDataSelector(getState()), { id: channelId });
      const range = currentChannel?.isVod ? 7 : 1;
      const timeByChannelType = currentChannel?.isVod
        ? moment(Date.now()).add(config.timeDiffFromServer, 'm')
        : moment(startTime);
      const start = moment(timeByChannelType).startOf('day').subtract(range, 'days');
      const end = moment(timeByChannelType).endOf('day').add(range, 'days');

      const isLoggedIn = getIsLoggedInSelector(getState());

      const endpointEPG = urlHelper.getAuthorizedEndpointUrl(
        api.endpoints.epg(channelId, start.toISOString(), end.toISOString()),
        isLoggedIn,
      );

      const programs = await requester({
        method: 'get',
        url: `${api.host}${endpointEPG}`,
      });

      let newProgramsForChannel = filter(programs, { channelId: channelId });

      if (currentChannel?.isVod) {
        newProgramsForChannel = filter(newProgramsForChannel, (program) => {
          return program.startTime > start.toISOString() && program.endTime < end.toISOString();
        });
      }

      // TEMP Because of EPG stub in prod
      const removedDuplicates = uniqBy(newProgramsForChannel, 'startTime');
      const epg = convertProgramsToEPGList(removedDuplicates);

      dispatch({ type: types.FETCH_EPG_SUCCESS, payload: { channelId, epg } });
    } catch (error) {
      console.error(error);
      dispatch({
        type: types.FETCH_EPG_ERROR,
        payload: { channelId, error: error.message },
      });
    }
  };

export const fetchExtraEPGItems =
  ({ channelId, direction }) =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const isLoggedIn = getIsLoggedInSelector(state);
      const currentChannel = find(getChannelsDataSelector(state), { id: channelId });

      if (!['next', 'prev'].includes(direction)) throw new Error('Unknown direction');

      if (direction === 'prev' && currentChannel.isFirstEpgItemLoaded) return;
      if (direction === 'next' && currentChannel.isLastEpgItemLoaded) return;
      if (currentChannel.isOnLazyload) return;

      dispatch({ type: types.FETCH_EXTRA_EPG_ITEMS, payload: { channelId } });
      // Need to cut off all headers, because only programs needed
      // + clone programs data because it will be mutated futher
      const oldPrograms = filter(currentChannel.epg, { isHeader: false }).map((p) => clone(p));
      const lastProgram = direction === 'next' ? last(oldPrograms) : head(oldPrograms);
      const { start, end } = getDateRange(lastProgram, direction === 'next');

      const endpointEPG = urlHelper.getAuthorizedEndpointUrl(
        api.endpoints.epg(channelId, start.toISOString(), end.toISOString()),
        isLoggedIn,
      );

      const newPrograms = await requester({
        method: 'get',
        url: `${api.host}${endpointEPG}`,
      });

      const newProgramsForChannel = filter(newPrograms, { channelId: channelId });

      const mergedPrograms = mergeArrays(oldPrograms, newProgramsForChannel, direction === 'next');
      // TEMP Because of EPG stub in prod
      const removedDuplicates = uniqBy(mergedPrograms, 'startTime');
      const epg = convertProgramsToEPGList(removedDuplicates);

      const isFirstEpgItemLoaded = direction === 'prev' && isEmpty(newProgramsForChannel);
      const isLastEpgItemLoaded = direction === 'next' && isEmpty(newProgramsForChannel);

      dispatch({
        type: types.FETCH_EXTRA_EPG_ITEMS_SUCCESS,
        payload: { channelId, epg, isFirstEpgItemLoaded, isLastEpgItemLoaded },
      });
    } catch (error) {
      console.error(error);
      dispatch({
        type: types.FETCH_EXTRA_EPG_ITEMS_ERROR,
        payload: { error: error.message, channelId },
      });
    }
  };

export const updateChannelsCurrentProgram =
  ({ ids }) =>
  async (dispatch, getState) => {
    if (isEmpty(ids)) return;

    const liveEPGErrorTimestamp = getLiveEPGErrorTimestamp(getState());
    // Wait 5 minues before ask EPG update since last failed request
    if (moment(Date.now()).diff(liveEPGErrorTimestamp, 'minutes') < 5) return;

    dispatch({ type: types.UPDATE_CURRENT_PROGRAM, payload: { ids } });
    try {
      const isLoggedIn = getIsLoggedInSelector(getState());
      const programs = await getCurrentProgramForChannelsByIds(ids, isLoggedIn);
      dispatch({
        type: types.UPDATE_CURRENT_PROGRAM_SUCCESS,
        payload: { programs, ids },
      });
    } catch (error) {
      console.error(error);
      dispatch({
        type: types.UPDATE_CURRENT_PROGRAM_ERROR,
        payload: { error: error.message },
      });
    }
  };

export const toggleChannelInFavoriteStatus = (channelId) => async (dispatch) => {
  try {
    dispatch({ type: types.TOGGLE_CHANNEL_IN_FAVORITE_STATUS, payload: { channelId } });
    const favoriteChannels = await getFavoriteChannelsList();
    let favorite = false;
    /*
     * Если нет списка с сохраненными каналами - нужно его создать
     * (создается сразу с нужным каналом)
     */
    if (!favoriteChannels) {
      await createChannelFavoritesList(channelId);
      favorite = true;
    } else {
      const { id: listId } = favoriteChannels;
      const isInFavorites = favoriteChannels.channelIds.includes(channelId);

      if (isInFavorites) {
        await requester({
          method: 'delete',
          url: `${api.host}${api.endpoints.favoriteChannels}/${listId}/channels/${channelId}`,
        });
      } else {
        await requester({
          method: 'post',
          url: `${api.host}${api.endpoints.favoriteChannels}/${listId}/channels`,
          data: { channelId },
        });
      }
      favorite = !isInFavorites;
    }
    const toastText = !favorite
      ? i18n.t('common.toast.channelFavoriteRemoved')
      : i18n.t('common.toast.channelFavoriteAdded');
    dispatch(showToast({ options: { toastText } }));
    dispatch({
      type: types.TOGGLE_CHANNEL_IN_FAVORITE_STATUS_SUCCESS,
      payload: {
        channelId,
        favorite,
      },
    });
  } catch (error) {
    console.log(error);
    dispatch({
      type: types.TOGGLE_CHANNEL_IN_FAVORITE_STATUS_ERROR,
      payload: { error: error.message },
    });
  }
};

export const toggleProgramInFavoriteStatus =
  ({ id, channelId }) =>
  async (dispatch, getState) => {
    try {
      const channels = getChannelsDataSelector(getState());
      const currentChannel = find(channels, { id: channelId }) || {};
      const currentProgram = find(currentChannel.epg, { id }) || {};
      const isInFavorite = currentProgram.favorite;

      dispatch({ type: types.TOGGLE_PROGRAM_IN_FAVORITE_STATUS, payload: { id, channelId } });

      await requester({
        method: isInFavorite ? 'delete' : 'post',
        url: isInFavorite
          ? `${api.host}${api.endpoints.favorites}/${currentProgram.vodAssetId}`
          : `${api.host}${api.endpoints.favorites}`,
        appendParams: true,
        data: { vodAssetId: currentProgram.vodAssetId },
      });

      const toastText = isInFavorite
        ? i18n.t('common.toast.programFavoriteRemoved')
        : i18n.t('common.toast.programFavoriteAdded');
      dispatch(showToast({ options: { toastText } }));
      dispatch({
        type: types.TOGGLE_PROGRAM_IN_FAVORITE_STATUS_SUCCESS,
        payload: { id, channelId, favorite: !isInFavorite },
      });
    } catch (error) {
      console.error(error);
      dispatch({
        type: types.TOGGLE_PROGRAM_IN_FAVORITE_STATUS_ERROR,
        payload: { error: error.message },
      });
    }
  };

export const resetAllChannelsData = () => ({ type: types.RESET_ALL_DATA });
